import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, m } from 'motion/react';
import { $app } from '../app/App';
import EmailForm from '../components/EmailForm';
import ExistingAccount, { ACCOUNT_VARIANT } from '../components/ExistingAccount';
import locales from '../locales.json';
import { ANIM_CONTENT, getContentTransition } from '../constants/layoutAnimations';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { REGISTRATION_STATUS } from '../helpers/registration';

const { EMAIL_TEXTS, API_TEXTS } = locales;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

const VIEW = {
  form: 'form',
  existingAccount: 'existingAccount',
  unverifiedAccount: 'unverifiedAccount',
};

const isAlreadyRegistered = (response) => response?.data.registration_ability === false;

// zakaznika i jeho stavy dohleda backend podle adresy. Kdyz se to nepovede tak se vrati false a zakaznik bude pokracovat v registraci.
const isIncompleteRegistration = async (email, signal) => {
  try {
    const status = await $app.getRegistrationStatus(email, { signal });

    return status?.status === REGISTRATION_STATUS.incomplete;
  } catch {
    return false;
  }
};

const EmailPage = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [view, setView] = useState(VIEW.form);
  const [direction, setDirection] = useState(1);
  const [email, setEmail] = useState('');
  const [knownEmail, setKnownEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  // Zrusi bezici pozadavek, kdyz uzivatel odejde ze stranky.
  useEffect(() => () => abortRef.current?.abort(), []);

  const goToView = (next) => {
    setDirection(next === VIEW.form ? -1 : 1);
    setView(next);
  };

  const handleChange = (event) => {
    setEmail(event.target.value);
    if (error) setError('');
  };

  const handleChangeEmail = () => goToView(VIEW.form);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const value = email.trim().toLowerCase();

    if (!value) {
      setError(EMAIL_TEXTS.errorEmpty);
      return;
    }

    if (!EMAIL_PATTERN.test(value)) {
      setError(EMAIL_TEXTS.errorFormat);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');

    try {
      if (!executeRecaptcha) {
        setError(API_TEXTS.recaptcha_failed);
        return;
      }
      const recaptcha_token = await executeRecaptcha('bandi_check_email');
      const response = await $app.checkRegistrationAbility('email', value, {
        signal: controller.signal,
      }, recaptcha_token);

      if (isAlreadyRegistered(response)) {
        setKnownEmail(value);
        goToView(VIEW.existingAccount);
        return;
      }
      // obrazovka 3b
      if (await isIncompleteRegistration(value, controller.signal)) {
        setKnownEmail(value);
        goToView(VIEW.unverifiedAccount);
        return;
      }

      navigate('/register', { state: { email: value } });
    } catch (err) {
      if (err.exception !== 'canceled') {
        setError(err.message ?? API_TEXTS.clientError);
      }
    } finally {
      abortRef.current = null;
      setLoading(false);
    }
  };

  const isKnownAccount = view === VIEW.existingAccount || view === VIEW.unverifiedAccount;

  return (
    <AnimatePresence mode="wait" initial={false} custom={direction}>
      <m.div
        key={view}
        custom={direction}
        variants={ANIM_CONTENT}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={getContentTransition(direction)}
      >
        {isKnownAccount ? (
          <ExistingAccount
            email={knownEmail}
            onChangeEmail={handleChangeEmail}
            variant={
              view === VIEW.unverifiedAccount
                ? ACCOUNT_VARIANT.unverified
                : ACCOUNT_VARIANT.registered
            }
          />
        ) : (
          <EmailForm
            email={email}
            error={error}
            loading={loading}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onBack={() => navigate('/')}
          />
        )}
      </m.div>
    </AnimatePresence>
  );
};

export default EmailPage;
